import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    DateRangePicker,
    Icon,
    Input,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Textarea,
} from "@elit-hair/elit-ui";
import { captureException } from "@sentry/react";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { TbAlertTriangle, TbArrowLeft, TbCheck, TbCopy, TbSearch } from "react-icons/tb";
import { toast } from "sonner";

import { Link as AppLink } from "@/components/link";
import { TeamMemberSelector } from "@/components/ui/team-member-selector";
import { CALL_ANSWER_STATUS_FILTERS, CallAnswerStatusFilter } from "@/types/call-answer-status-filter.types";
import { convertFromDateRange } from "@/utils/format-date";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

import {
    ALL_ANSWER_STATUSES_VALUE,
    ANSWER_STATUS_LABELS,
    MAX_PHONE_NUMBERS,
} from "./constants/calls-by-phone.constants";
import { CallsByPhoneFilters, LOOKUP_STATUS, PhoneNumberLookupResult } from "./types/calls-by-phone.types";
import { fetchCallsByPhoneNumber, parsePhoneNumbers } from "./utils/calls-by-phone.utils";

export const Route = createFileRoute(APP_ROUTES_FULL.DEVELOPER_CALLS_BY_PHONE)({
    component: CallsByPhonePage,
});

function LookupStatusIcon({ status }: { status: LOOKUP_STATUS }) {
    if (status === LOOKUP_STATUS.LOADING) return <Spinner size="sm" />;
    if (status === LOOKUP_STATUS.ERROR) return <Icon icon={TbAlertTriangle} className="text-destructive" />;

    return <Icon icon={TbCheck} className="text-green-600" />;
}

function CallsByPhonePage() {
    const [rawPhoneNumbers, setRawPhoneNumbers] = useState("");
    const [agentId, setAgentId] = useState<string | undefined>();
    const [dateRange, setDateRange] = useState<DateRange | undefined>();
    const [answerStatus, setAnswerStatus] = useState<CallAnswerStatusFilter | undefined>();
    const [durationMin, setDurationMin] = useState("");
    const [durationMax, setDurationMax] = useState("");
    const [results, setResults] = useState<PhoneNumberLookupResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    const parsedPhoneNumbers = useMemo(() => parsePhoneNumbers(rawPhoneNumbers), [rawPhoneNumbers]);
    const exceedsMax = parsedPhoneNumbers.length > MAX_PHONE_NUMBERS;

    const filters: CallsByPhoneFilters = useMemo(() => {
        const { startDate, endDate } = convertFromDateRange(dateRange);

        return {
            userId: agentId,
            startDate,
            endDate,
            answerStatus,
            callDurationMin: durationMin ? Number(durationMin) : undefined,
            callDurationMax: durationMax ? Number(durationMax) : undefined,
        };
    }, [agentId, dateRange, answerStatus, durationMin, durationMax]);

    const allCallIds = useMemo(() => results.flatMap((result) => result.callIds), [results]);
    const completedCount = results.filter((result) => result.status !== LOOKUP_STATUS.LOADING).length;

    const handleSearch = useCallback(async () => {
        setIsSearching(true);
        setResults(
            parsedPhoneNumbers.map((phoneNumber) => ({
                phoneNumber,
                status: LOOKUP_STATUS.LOADING,
                callIds: [],
            }))
        );

        for (const phoneNumber of parsedPhoneNumbers) {
            try {
                const calls = await fetchCallsByPhoneNumber(phoneNumber, filters);
                const callIds = calls.map((call) => call.id);

                setResults((prev) =>
                    prev.map((result) =>
                        result.phoneNumber === phoneNumber
                            ? { ...result, status: LOOKUP_STATUS.SUCCESS, callIds }
                            : result
                    )
                );
            } catch (error) {
                captureException(error);
                setResults((prev) =>
                    prev.map((result) =>
                        result.phoneNumber === phoneNumber ? { ...result, status: LOOKUP_STATUS.ERROR } : result
                    )
                );
            }
        }

        setIsSearching(false);
    }, [parsedPhoneNumbers, filters]);

    const handleCopyAllCallIds = useCallback(async () => {
        await navigator.clipboard.writeText(allCallIds.join("\n"));
        toast.success(`${allCallIds.length} call ids copied`);
    }, [allCallIds]);

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
                <AppLink
                    to={APP_ROUTES_TO.DEVELOPER}
                    className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                    <Icon icon={TbArrowLeft} />
                    Testing
                </AppLink>
                <div>
                    <h1 className="text-2xl font-semibold">Calls by phone number</h1>
                    <p className="text-muted-foreground">
                        Paste up to {MAX_PHONE_NUMBERS} phone numbers and get the call ids for each one of them.
                    </p>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base font-medium">Phone numbers</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="phone-numbers">One per line, or separated by commas or semicolons</Label>
                        <Textarea
                            id="phone-numbers"
                            value={rawPhoneNumbers}
                            onChange={(event) => setRawPhoneNumbers(event.target.value)}
                            placeholder={"+49 171 1234567\n+90 555 1234567"}
                            rows={8}
                            className="font-mono text-sm"
                        />
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <div className="flex flex-col gap-2">
                            <Label>Agent</Label>
                            <TeamMemberSelector
                                selectedMemberId={agentId ?? null}
                                onMemberChange={(memberId) => setAgentId(memberId ?? undefined)}
                                labels={{ resetSelection: "all_agents" }}
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <Label>Date range</Label>
                            <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} showClearButton />
                        </div>
                        <div className="flex flex-col gap-2">
                            <Label>Answer status</Label>
                            <Select
                                value={answerStatus ?? ALL_ANSWER_STATUSES_VALUE}
                                onValueChange={(value) =>
                                    setAnswerStatus(
                                        value === ALL_ANSWER_STATUSES_VALUE
                                            ? undefined
                                            : CALL_ANSWER_STATUS_FILTERS.find((status) => status === value)
                                    )
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ALL_ANSWER_STATUSES_VALUE}>All</SelectItem>
                                    {CALL_ANSWER_STATUS_FILTERS.map((status) => (
                                        <SelectItem key={status} value={status}>
                                            {ANSWER_STATUS_LABELS[status]}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="duration-min">Min duration (s)</Label>
                            <Input
                                id="duration-min"
                                type="number"
                                min={0}
                                value={durationMin}
                                onChange={(event) => setDurationMin(event.target.value)}
                                placeholder="0"
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="duration-max">Max duration (s)</Label>
                            <Input
                                id="duration-max"
                                type="number"
                                min={0}
                                value={durationMax}
                                onChange={(event) => setDurationMax(event.target.value)}
                                placeholder="No limit"
                            />
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <Button
                            onClick={handleSearch}
                            disabled={parsedPhoneNumbers.length === 0 || exceedsMax || isSearching}
                        >
                            {isSearching ? <Spinner size="sm" /> : <Icon icon={TbSearch} />}
                            <span className="ml-2">Search calls</span>
                        </Button>
                        <span className={exceedsMax ? "text-sm text-destructive" : "text-sm text-muted-foreground"}>
                            {exceedsMax
                                ? `${parsedPhoneNumbers.length} unique numbers, only ${MAX_PHONE_NUMBERS} are allowed`
                                : `${parsedPhoneNumbers.length} unique numbers`}
                        </span>
                        {isSearching && (
                            <span className="text-sm text-muted-foreground tabular-nums">
                                {completedCount} of {results.length} done
                            </span>
                        )}
                    </div>
                </CardContent>
            </Card>

            {results.length > 0 && (
                <Card>
                    <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
                        <CardTitle className="text-base font-medium">
                            {allCallIds.length} call ids across {results.length} numbers
                        </CardTitle>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleCopyAllCallIds}
                                disabled={allCallIds.length === 0}
                            >
                                <Icon icon={TbCopy} />
                                <span className="ml-2">Copy all call ids</span>
                            </Button>
                            <Button variant="outline" size="sm" asChild>
                                <AppLink to={APP_ROUTES_TO.DEVELOPER_BULK_CALL_ACTIONS}>Open bulk call actions</AppLink>
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12" />
                                    <TableHead>Phone number</TableHead>
                                    <TableHead className="w-20 text-right">Calls</TableHead>
                                    <TableHead>Call ids</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {results.map((result) => (
                                    <TableRow key={result.phoneNumber}>
                                        <TableCell>
                                            <LookupStatusIcon status={result.status} />
                                        </TableCell>
                                        <TableCell className="font-mono text-sm whitespace-nowrap">
                                            {result.phoneNumber}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {result.status === LOOKUP_STATUS.ERROR ? "—" : result.callIds.length}
                                        </TableCell>
                                        <TableCell className="font-mono text-xs break-all text-muted-foreground">
                                            {result.callIds.join(", ")}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
